import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import './account-screen.styles.css';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { languages } from '@/constants/languages';
import SwitchButton from '@/components/buttons/SwitchButton';
import { MdInfo } from 'react-icons/md';
import { Tooltip } from 'primereact/tooltip';
import { X4bUserSettingDto } from './X4bUserSettingDto';
import lodash from 'lodash';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Column, Row } from '@/components/container/Container';
import { TextBodySmall, TextLabelLarge2, TextLabelMedium2 } from '@/components/text/Texts';
import AddDomainSection from './AddDomainSection';
import { InputController } from '@/components/inputs/InputController';
import DropdownInput from '@/components/inputs/DropdownInput';

type Props = {
	settingQ: ReturnType<typeof import('@tanstack/react-query').useQuery<any, any>>;
};

const SettingsGeneralTab = ({ settingQ }: Props) => {
	const formId = 'UserSettingsForm';
	const form = useForm<X4bUserSettingDto>();

	useEffect(() => {
		if (settingQ.data) form.reset(settingQ.data.Data);
	}, [settingQ.data, form]);

	var debounced = lodash.debounce(async () => {
		const toPost = {
			SystemLanguageCode: form.getValues().SystemLanguageCode,
			AutoApproveFromTrustedDomains: form.getValues().AutoApproveFromTrustedDomains,
			AllowDiscoverability: form.getValues().AllowDiscoverability,
		} as X4bUserSettingDto;
		await axiosInstance.post<X4bUserSettingDto>(ENDPOINTS['/api/X4bUser/UpdateUserSetting'], toPost);
		showNotification("Company info updated");
	}, 500);

	useEffect(() => {
		const subscription = form.watch(async (values, { name, type }) => {
			if (type === "change") {
				debounced();
				//console.log("SystemLanguageCode changed:", values.SystemLanguageCode);
			}
		});
		return () => subscription.unsubscribe();
	}, [form]);

	return (
		<Column className="gap-6">
			<Column className="gap-4">
				<TextLabelLarge2 className="font-semibold text-primary" text="System Language" />
				<TextLabelMedium2
					className="text-primary"
					text="This will show up as the language in statements and emails."
				/>
			</Column>

			<InputController id="SystemLanguageCode" control={form.control}>
				<DropdownInput
					label="Language"
					options={languages}
					optionLabel="Name"
					optionValue="Code"
					containerCls="w-[516px]"
				/>
			</InputController>

			<div className="w-full h-[1px] bg-neutralVariant95" />

			<Column className="gap-4">
				<TextLabelLarge2 className="font-semibold text-primary" text="Opt-In Invite Settings" />
				<TextLabelMedium2
					className="text-primary"
					text="When you share an invite link for people to join your team, they'll be prompted to enter an email address. You can configure how requests from trusted domains and other domains are handled."
				/>
			</Column>

			<Column className="gap-4">
				<TextLabelLarge2 className="font-semibold text-primary" text="Trusted Domains" />
				<TextLabelMedium2 className="text-primary" text="Add domains from your organisation" />
			</Column>

			<AddDomainSection />

			<Row className="gap-3 items-center py-2.5">
				<Controller
					name="AutoApproveFromTrustedDomains"
					control={form.control}
					render={({ field }) => (
						<SwitchButton
							checked={!!field.value}
							setChecked={field.onChange}
						/>
					)}
				/>
				<TextLabelMedium2 className="text-primary" text="Automatically approve request from trusted domains." />
				<Tooltip
					target="#auto-approve-info"
					position="top"
					pt={{
						root: { className: 'p-0' },
						text: { className: 'w-[554px] h-[116px] bg-neutral30 px-2 py-1 flex flex-col justify-between' },
					}}
				>
					<>
						<TextBodySmall
							className="text-inverseOnSurface"
							text="Automatic approval allows people who join via an invite link to access Xpress for Business instantly, without requiring any additional steps from you."
						/>
						<TextBodySmall
							className="text-inverseOnSurface"
							text="If automatic approval is disabled, you will need to manually approve requests through the Awaiting Approval tab on the People page."
						/>
					</>
				</Tooltip>
				<MdInfo
					id="auto-approve-info"
					size={24}
					className="text-black cursor-pointer"
				/>
			</Row>

			<div className="w-full h-[1px] bg-neutralVariant95" />

			<Column className="gap-4">
				<TextLabelLarge2 className="font-semibold text-primary" text="Opt-In Discoverability" />
				<TextLabelMedium2
					className="text-primary"
					text="Allow users with trusted domains to use their Xpress account to search for or request the opt-in link when creating a business profile."
				/>
			</Column>

			<Row className="gap-3 items-center py-2.5">
				<Controller
					name="AllowDiscoverability"
					control={form.control}
					render={({ field }) => (
						<SwitchButton
							checked={!!field.value}
							setChecked={field.onChange}
						/>
					)}
				/>

				<TextLabelMedium2 className="text-primary" text="Allow discoverability." />
				<Tooltip
					target="#discoverability-info"
					position="top"
					pt={{
						root: { className: 'p-0' },
						text: { className: 'w-[384px] h-[48px] bg-neutral30 px-2 py-1' },
					}}
				>
					<TextBodySmall
						className="text-inverseOnSurface"
						text="Disabling this feature restricts opt-in access to users who have been directly sent the opt-in link."
					/>
				</Tooltip>
				<MdInfo
					id="discoverability-info"
					size={24}
					className="text-black cursor-pointer"
				/>
			</Row>
		</Column>
	);
};

export default SettingsGeneralTab;
